<template>
  <div
    class="coupon-detail"
    :class="{
      'coupon-detail--disabled': disabled
    }"
    ref="couponDetailRef"
    v-if="coupon"
  >
    <div
      class="coupon-detail-preview"
      ref="couponDetailPreviewRef"
      v-if="!show"
      @click="onPreviewClick"
    >
      <img
        class="coupon-detail-preview__gift"
        src="https://cdn-image.bestdealer.com/app-bestdealer/1688/coupon/gift.png"
      >
      <div class="coupon-detail-preview__content">
        <div class="coupon-detail-preview__title">
          <IntlCurrency
            class="coupon-detail-preview__currency"
            currency="USD"
          />
          <span class="coupon-detail-preview__amount">{{ coupon.discountAmount }}</span>
          Off
          <Countdown
            class="coupon-detail-preview__time"
            :time="coupon.endTimeUser"
            @end="reset"
          />
        </div>
        <span class="coupon-detail-preview__divider" />
        <span class="coupon-detail-preview__text">Total items over US ${{ coupon.minOrderAmount }}</span>
      </div>
      <Icon
        class="coupon-detail-preview__arrow"
        name="arrow-right-l-2"
      />
    </div>
    <div
      class="coupon-detail__content"
      ref="couponDetailContentRef"
      v-else
    >
      <div class="coupon-detail__header">
        <p class="coupon-detail__title">
          Spend & Save Coupon
        </p>
        <Icon
          @click="show = false"
          class="coupon-detail__close"
          name="arrow-up-l-2"
        />
      </div>
      <div class="coupon-detail-coupon">
        <div class="coupon-detail-coupon__left">
          <div class="coupon-detail__off">
            <IntlCurrency
              class="coupon-detail__currency"
              currency="USD"
            />
            <span class="coupon-detail__amount">{{ coupon.discountAmount }}</span>
            Off
          </div>
          <p class="coupon-detail__total">
            Total items over US ${{ coupon.minOrderAmount }}
          </p>
          <p class="coupon-detail__extra">
            {{ coupon.displayText }}
          </p>
        </div>
        <div class="coupon-detail-coupon__right">
          <p class="coupon-detail__available">
            Available
          </p>
          <Countdown
            class="coupon-detail__valid"
            :time="coupon.endTimeUser"
            @end="reset"
          />
        </div>
      </div>
      <div
        class="coupon-detail-delete__wrapper"
        @click="invalidateCoupon(coupon.couponCode)"
      >
        <p class="coupon-detail-delete">
          <img
            class="coupon-detail-delete__icon"
            src="@/assets/colorIcon/delete.svg"
          >
          <span class="coupon-detail-delete__text">Delete</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import { useCoupon } from '@/views/Carts/hooks/useCoupon'
import { Countdown } from '@/components/Countdown'
import { IntlCurrency } from '@/components/NewIntlCurrency'

const { coupon, invalidateCoupon, reset } = useCoupon()

const show = ref(false)
const couponDetailRef = ref(null)
const couponDetailPreviewRef = ref(null)
const couponDetailContentRef = ref(null)

defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

watch(show, () => {
  const el = couponDetailRef.value
  if (!el) return
  const contentHeight = (show.value ? couponDetailPreviewRef.value : couponDetailContentRef.value)?.offsetHeight
  if (contentHeight) el.style.height = `${contentHeight}px`

  nextTick(() => {
    const contentHeight = (show.value ? couponDetailContentRef.value : couponDetailPreviewRef.value)?.offsetHeight
    if (contentHeight) el.style.height = `${contentHeight}px`
  })
})

const onPreviewClick = () => {
  show.value = true
}
</script>

<style lang="less" scoped>
.coupon-detail {
  --opacity: 1;

  position: relative;
  border-radius: 8px 8px 0 0;
  background-color: #F0F9FD;
  font-family: mon-600;
  font-size: 12px;
  color: #052784;
  transition: height .3s ease-in-out;

  &:has(.coupon-detail__content) {
    overflow: hidden;
  }

  &--disabled {
    --opacity: .5;

    background: #EBEBEB;

    .coupon-detail-preview {
      filter: grayscale(1);
      opacity: 0.5;
    }

    .coupon-detail__content {
      filter: grayscale(1);
    }
  }

  @media (min-width: 1024px) {
    border-radius: 4px;
    font-size: 14px;
  }

  &-preview {
    display: flex;
    align-items: center;
    padding: 10px 8px;
    cursor: pointer;

    @media (min-width: 1024px) {
      padding: 12px 16px;
    }

    &__gift {
      margin-right: 8px;
      width: 24px;
      height: 24px;

      @media (min-width: 1024px) {
        width: 36px;
        height: 36px;
      }
    }

    &__content {
      display: flex;
      align-items: center;
      gap: 2px;

      @media (min-width: 1024px) {
        align-items: flex-start;
        flex-direction: column;
      }
    }

    &__title {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 18px;
    }

    &__amount {
      font-family: mon-700;
      padding-left: 3px;
      padding-right: 3px;
      font-size: 16px;
      color: #F55745;
    }

    &__time {
      margin-left: 8px;
    }

    &__text {
      font-size: 12px;
      background: linear-gradient(90deg, #052784 0%, #E14D39 100%);
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
    }

    &__arrow {
      margin-left: auto;

      @media (min-width: 1024px) {
        width: 14px;
        height: 14px;
        color: #232323;
      }
    }
  }

  &__content {
    padding: 12px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-family: mon-700;
    font-size: 14px;
    line-height: 18px;
    opacity: var(--opacity);
  }

  &__currency {
    font-size: 14px;
  }

  &__close {
    cursor: pointer;
  }

  &-coupon {
    /** Coupon style */
    mask-image: radial-gradient(circle at 58% 8px, transparent 8px, red 8.5px), linear-gradient(transparent 25%, red 0px, red 75%, transparent 0px);
    mask-size: 100%, 1px 8px;
    mask-repeat: repeat, repeat-y;
    mask-position: 0px -8px, 58% center;
    mask-composite: subtract;
    background-color: #ffffff;
    border-radius: 6px;
    opacity: var(--opacity);

    margin-top: 10px;
    position: relative;
    display: flex;
    height: 88px;

    @media (min-width: 1024px) {
      height: 66px;
    }

    &__left, &__right {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      text-align: center;
      padding: 0 4px;
    }

    &__left {
      width: 58%;
    }

    &__right {
      width: 42%;
    }
  }

  &__off {
    display: flex;
    align-items: center;
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
  }

  &__amount {
    padding: 0 2px;
    font-family: mon-700;
    color: #F55745;
    font-size: 20px;
    line-height: 24px;
  }

  &__total {
    font-family: mon-600;
    font-size: 10px;
    line-height: 14px;
  }

  &__extra {
    font-family: mon-600;
    font-size: 10px;
    line-height: 14px;
  }

  &__available {
    padding: 1px 11px;
    border-radius: 20px;
    border: 1px solid #EBF1F5;
    font-family: mon-500;
    font-size: 10px;
    line-height: 16px;
    color: #232323;
  }

  &__valid {
    margin-top: 4px;
  }

  &-delete {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    &__wrapper {
      text-align: center;
      height: 14px;
      margin-top: 12px;
    }

    &__icon {
      width: 16px;
      height: 16px;

      @media (min-width: 1024px) {
        width: 12px;
        height: 12px;
      }
    }

    &__text {
      font-family: mon-500;
      font-size: 12px;
      text-decoration: underline;
      color: #232323;

      @media (min-width: 1024px) {
        font-size: 10px;
      }
    }
  }
}
</style>
